import React, { useState, useEffect } from "react";
import {
  Box, Button, Flex, Heading, HStack, Input, Spinner,
  Table, TableContainer, Tbody, Td, Th, Thead, Tr,
} from "@chakra-ui/react";
import { Company } from "../../../Interfaces/Company";
import { getAmbulanceCountByCompanies } from "../../../Http/Index";

interface TableDataProps {
  data: Company[];
  onEdit: (company: Company) => void;
  onAddClick: () => void;
}

const PAGE_SIZE = 10;

const TableData: React.FC<TableDataProps> = ({ data, onEdit, onAddClick }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [ambulanceCounts, setAmbulanceCounts] = useState<Record<string, number>>({});
  const [countsLoading, setCountsLoading] = useState(false);

  const filtered = data.filter((c) =>
    c.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.subscriptionType?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Fetch ambulance counts whenever the visible set of companies changes
  useEffect(() => {
    if (paginated.length === 0) return;

    const ids = paginated.map((c) => c._id);

    setCountsLoading(true);
    getAmbulanceCountByCompanies(ids)
      .then((res) => setAmbulanceCounts(res.data))
      .catch(() => {/* keep previous counts on error */})
      .finally(() => setCountsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchQuery, data]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  return (
    <Box p={6} bg="white" borderRadius="lg" shadow="sm" borderWidth={1} mt={4}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="md" color="teal.600">Companies</Heading>
        <HStack spacing={4}>
          <Input
            placeholder="Search by name or subscription type..."
            value={searchQuery}
            onChange={handleSearch}
            w="280px"
            bg="gray.50"
          />
          <Button colorScheme="green" onClick={onAddClick}>+ Add Company</Button>
        </HStack>
      </Flex>

      <TableContainer>
        <Table variant="simple" colorScheme="teal">
          <Thead bg="gray.50">
            <Tr>
              <Th>Sl.No</Th>
              <Th>Company Name</Th>
              <Th>Subscription Type</Th>
              <Th>Date of Partnership</Th>
              <Th>Contract Renewal Date</Th>
              <Th isNumeric>
                Ambulance Count
                {countsLoading && <Spinner size="xs" ml={2} />}
              </Th>
              <Th textAlign="right">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {paginated.map((company, index) => (
              <Tr key={company._id} _hover={{ bg: "gray.50" }}>
                <Td>{(currentPage - 1) * PAGE_SIZE + index + 1}</Td>
                <Td fontWeight="medium">{company.companyName}</Td>
                <Td>{company.subscriptionType}</Td>
                <Td>{(company as any).dateOfPartnership}</Td>
                <Td>{(company as any).contractRenewalDate}</Td>
                <Td isNumeric>
                  {countsLoading ? "—" : (ambulanceCounts[company._id] ?? "—")}
                </Td>
                <Td>
                  <Flex justify="flex-end">
                    <Button size="sm" colorScheme="blue" variant="outline" onClick={() => onEdit(company)}>
                      Edit
                    </Button>
                  </Flex>
                </Td>
              </Tr>
            ))}
            {paginated.length === 0 && (
              <Tr>
                <Td colSpan={7} textAlign="center" py={6} color="gray.500">
                  No companies found.
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Flex justify="center" align="center" mt={4} gap={2}>
          <Button size="sm" onClick={() => setCurrentPage((p) => p - 1)} isDisabled={currentPage === 1}>Prev</Button>
          {Array.from({ length: totalPages }, (_, i) => (
            <Button
              key={i + 1}
              size="sm"
              variant={currentPage === i + 1 ? "solid" : "outline"}
              colorScheme="teal"
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </Button>
          ))}
          <Button size="sm" onClick={() => setCurrentPage((p) => p + 1)} isDisabled={currentPage === totalPages}>Next</Button>
        </Flex>
      )}
    </Box>
  );
};

export default TableData;
